import React from 'react';
import Card from '../Card';
import MetricCardGrid from '../MetricCardGrid';
import DataTable, { TableColumn } from '../DataTable';
import { ResourceMetrics, TaskMetrics } from '../../utils/reportingUtils';

interface ResourcesTabProps {
  resourceMetrics: ResourceMetrics;
  taskMetrics: TaskMetrics;
}

interface UserWorkloadRow {
  userName: string;
  taskCount: number;
  overdueCount: number;
  workloadPercent: number;
  isOverloaded: boolean;
  hasOverdue: boolean;
}

const ResourcesTab: React.FC<ResourcesTabProps> = ({ resourceMetrics, taskMetrics }) => {
  const summaryMetrics = [
    {
      value: resourceMetrics.totalUsers,
      label: 'Total Users',
      color: 'primary' as const
    },
    {
      value: resourceMetrics.activeUsers,
      label: 'Active Users',
      color: 'primary' as const
    },
    {
      value: resourceMetrics.totalUsers > 0 
        ? (taskMetrics.totalTasks / resourceMetrics.totalUsers).toFixed(1)
        : '0',
      label: 'Avg Tasks per User',
      color: 'primary' as const
    }
  ];

  const workloadData: UserWorkloadRow[] = resourceMetrics.userWorkload.map(user => ({
    userName: user.userName,
    taskCount: user.taskCount,
    overdueCount: user.overdueCount,
    workloadPercent: (user.taskCount / taskMetrics.totalTasks) * 100,
    isOverloaded: user.taskCount > 5,
    hasOverdue: user.overdueCount > 0
  }));

  const workloadColumns: TableColumn<UserWorkloadRow>[] = [
    {
      key: 'userName',
      label: 'User',
      render: (row) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">{row.userName}</span>
      )
    },
    {
      key: 'taskCount',
      label: 'Active Tasks',
      render: (row) => (
        <span className="text-gray-700 dark:text-gray-300">{row.taskCount}</span>
      )
    },
    {
      key: 'overdueCount',
      label: 'Overdue Tasks',
      render: (row) => (
        <span className={row.hasOverdue ? 'text-error font-bold' : 'text-gray-600 dark:text-gray-400'}>
          {row.overdueCount}
        </span>
      )
    },
    {
      key: 'workload',
      label: 'Workload',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="progress-bar flex-1">
            <div
              className="progress-bar-fill"
              style={{
                width: `${row.workloadPercent}%`,
                backgroundColor: row.isOverloaded ? '#ef4444' : '#3b82f6',
              }}
            ></div>
          </div>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {row.workloadPercent.toFixed(0)}%
          </span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        if (row.isOverloaded) {
          return (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-error/15 text-error">
              ⚠️ Overloaded
            </span>
          );
        }
        if (row.taskCount === 0) {
          return (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
              Available
            </span>
          );
        }
        return (
          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-success/15 text-success">
            ✓ Normal
          </span>
        );
      }
    }
  ];

  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-6">Resource Capacity Analysis</h2>

      <MetricCardGrid metrics={summaryMetrics} columns={3} className="mb-8" />

      <Card>
        <Card.Body>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">User Workload Distribution</h3>
          <DataTable 
            columns={workloadColumns}
            data={workloadData}
            emptyMessage="No users found"
            getRowKey={(row) => row.userName}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default ResourcesTab;
